import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../services/api";
import HistoricalDataImportModal from "../../features/profile/components/HistoricalDataImportModal";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const navigate = useNavigate();

  const cleanName = useMemo(() => (name || "").trim(), [name]);
  const cleanEmail = useMemo(() => (email || "").trim().toLowerCase(), [email]);

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const pickNumber = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      const n = Number(String(v ?? "").replace(",", "."));
      if (Number.isFinite(n)) return n;
    }
    return NaN;
  };

  const pickString = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (typeof v === "string" && v.trim() !== "") return v.trim();
    }
    return "";
  };

  const isProfileComplete = (profile) => {
    if (!profile || typeof profile !== "object") return false;

    const name = pickString(profile, ["displayName", "display_name", "name", "nome"]);
    const gender = pickString(profile, ["gender", "sexo", "sex"]);
    const ageRange = pickString(profile, ["ageRange", "age_range", "faixaEtaria"]);
    const height = pickNumber(profile, [
      "height",
      "heightCm",
      "height_cm",
      "altura",
      "alturaCm",
    ]);
    const startWeight = pickNumber(profile, [
      "startWeight",
      "start_weight",
      "initialWeight",
      "initial_weight",
      "pesoInicial",
    ]);

    // Verifica se TODOS os campos da seção "Sobre Mim" foram preenchidos
    return Boolean(
      name && name.trim() !== "" &&
      gender && gender.trim() !== "" &&
      ageRange && ageRange.trim() !== "" &&
      Number.isFinite(height) && height > 0 &&
      Number.isFinite(startWeight) && startWeight > 0
    );
  };

  const extractProfile = (data) => {
    return data?.profile || data?.user?.profile || data?.data?.profile || data || null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");

    if (!cleanName || !cleanEmail || !password) {
      setError("Preencha nome, e-mail e senha para criar sua conta.");
      return;
    }

    if (!isValidEmail(cleanEmail)) {
      setError("Digite um e-mail válido para continuar.");
      return;
    }

    if (password.length < 6) {
      setError("A senha precisa ter pelo menos 6 caracteres.");
      return;
    }

    if (!acceptTerms) {
      setError("Para criar sua conta, você precisa aceitar os termos de serviço do MariaM.");
      return;
    }

    try {
      setLoading(true);

      const response = await api.post("/auth/register", {
        name: cleanName,
        email: cleanEmail,
        password,
      });

      const token = response?.data?.token;

      if (!token) {
        setError("Conta criada com sucesso! Faça login para continuar.");
        navigate("/login", { replace: true });
        return;
      }

      localStorage.setItem("token", token);

      let complete = false;
      try {
        const prof = await api.get("/users/profile");
        const p = extractProfile(prof?.data);

        try {
          localStorage.setItem("profile", JSON.stringify(p || {}));
        } catch {
          // sem ação
        }

        complete = isProfileComplete(p || {});
      } catch {
        complete = false;
      }

      navigate(complete ? "/app/home" : "/app/profile", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const apiMessage = err?.response?.data?.message;

      if (status === 409) {
        setError("Esse e-mail já está cadastrado. Entre com sua conta.");
      } else if (!err?.response) {
        setError("Não consegui conectar no servidor. Confira se o backend está rodando.");
      } else if (apiMessage) {
        setError(apiMessage);
      } else {
        setError("Não foi possível criar sua conta agora. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] text-[#24152F] flex flex-col justify-between p-4 sm:p-6 antialiased selection:bg-[#ECE7FA]">
      {/* Botão de Voltar */}
      <div className="w-full max-w-md mx-auto">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-xs font-bold text-[#6C54D8] hover:text-[#24152F] transition-colors py-2 group"
        >
          <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
          Voltar para início
        </button>
      </div>

      {/* Card Principal de Registro Liti */}
      <div className="w-full max-w-md mx-auto my-auto py-6">
        <div className="liti-card rounded-[28px] p-6 sm:p-8 shadow-xl border border-[#ECE7FA] bg-white">
          {/* Logo e Cabeçalho */}
          <div className="text-center mb-6">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#6C54D8] text-white font-black text-2xl shadow-sm mb-3">
              m
            </div>
            <h1 className="text-2xl font-bold text-[#24152F] tracking-tight font-serif">
              Criar Sua Conta
            </h1>
            <p className="mt-1 text-xs text-[#6D6680] font-medium">
              Comece sua jornada de autocuidado com o MariaM
            </p>
          </div>

          {/* Erros */}
          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-center text-xs font-semibold text-red-700 shadow-sm animate-in fade-in duration-200">
              {error}
            </div>
          )}

          {/* Formulário */}
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1.5">
                Nome Completo
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Como prefere ser chamado(a)?"
                autoComplete="name"
                disabled={loading}
                required
                className="input-base w-full h-12 text-sm font-medium"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1.5">
                E-mail
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                autoComplete="email"
                disabled={loading}
                inputMode="email"
                required
                className="input-base w-full h-12 text-sm font-medium"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1.5">
                Senha (mínimo 6 caracteres)
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Crie uma senha segura"
                  autoComplete="new-password"
                  disabled={loading}
                  minLength={6}
                  required
                  className="input-base w-full h-12 text-sm font-medium pr-12"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={loading}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-[#6C54D8] transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Checkbox Termos */}
            <div className="flex items-start gap-2.5 pt-2">
              <input
                id="accept-terms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded-md border-gray-300 text-[#6C54D8] focus:ring-[#6C54D8] accent-[#6C54D8] cursor-pointer shrink-0"
              />
              <label htmlFor="accept-terms" className="text-xs font-medium text-[#24152F] leading-relaxed cursor-pointer">
                Aceito os{" "}
                <button
                  type="button"
                  onClick={() => setIsTermsModalOpen(true)}
                  className="font-bold text-[#6C54D8] hover:underline"
                >
                  Termos de Uso e Privacidade
                </button>{" "}
                do MariaM.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-12 text-sm font-bold shadow-md uppercase tracking-wider flex items-center justify-center gap-2 mt-3"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Criando Conta...
                </>
              ) : (
                "CRIAR MINHA CONTA"
              )}
            </button>
          </form>

          {/* OPÇÃO DE IMPORTAR HISTÓRICO NO CADASTRO */}
          <div className="mt-4 pt-4 border-t border-[#ECE7FA] text-center">
            <button
              type="button"
              onClick={() => setIsImportModalOpen(true)}
              className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-[#6C54D8] hover:underline transition-colors"
            >
              <span>📊</span> Possui um histórico prévio? Importar planilha (CSV)
            </button>
          </div>

          {/* Rodapé do Card */}
          <div className="mt-4 pt-3 text-center">
            <p className="text-xs text-[#6D6680] font-medium">
              Já possui uma conta?{" "}
              <Link to="/login" className="font-bold text-[#6C54D8] hover:underline">
                Fazer Login
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Modal de Termos */}
      {isTermsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#24152F]/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="liti-card w-full max-w-sm rounded-[28px] p-6 shadow-2xl border border-[#ECE7FA] bg-white space-y-4">
            <h3 className="text-lg font-bold text-[#24152F] font-serif">
              Sobre o MariaM
            </h3>
            <div className="text-xs text-[#6D6680] font-medium leading-relaxed space-y-2.5 max-h-60 overflow-y-auto pr-1">
              <p>
                O MariaM é uma aplicação de autocuidado diário criada para ajudar você a organizar sua rotina de saúde, medicamentos, água e biometria de forma simples e segura.
              </p>
              <p>
                Seus dados são protegidos e utilizados exclusivamente para personalizar sua experiência dentro da plataforma.
              </p>
            </div>
            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setIsTermsModalOpen(false)}
                className="btn-primary text-xs font-bold h-9 px-5"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Importação CSV */}
      <HistoricalDataImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => setIsImportModalOpen(false)}
      />

      {/* Footer simples */}
      <div className="w-full max-w-md mx-auto text-center py-2">
        <p className="text-[11px] text-[#6D6680] font-medium">
          © {new Date().getFullYear()} MariaM — Saúde, bem-estar e resultados reais.
        </p>
      </div>
    </div>
  );
}